import { type Job, aiTagRequestSchema, jobSchema } from '@xphoto/shared';
import type { z } from 'zod';
import { apiFetch } from '../lib/http';
import { AiApiError } from './ai-api';

/**
 * Corps de requête côté client = type d'ENTRÉE du schéma (les champs à défaut, comme
 * `maxTags`/`dryRun`, y sont optionnels ; le serveur applique le défaut). L'appelant
 * n'a donc à fournir que `scope` (+ `folder`/`assetIds` selon la portée).
 */
export type AiTagRequestInput = z.input<typeof aiTagRequestSchema>;

/**
 * Client typé du TAGGING IA (T5 lot C). Enfile un job `ai_tag` qui propose des tags
 * par le LLM de vision configuré. La réponse (le `Job` enfilé) est re-parsée via son
 * schéma partagé. `AiApiError` porte le statut + code (ex. 409 `AI_PROVIDER_MISSING`,
 * capté par l'UI pour renvoyer vers « Configuration LLM »).
 */

async function errorCodeOf(res: Response): Promise<string> {
  try {
    const body = (await res.json()) as { error?: { code?: string } };
    if (typeof body?.error?.code === 'string') return body.error.code;
  } catch {
    // Corps non-JSON : code générique.
  }
  return 'UNKNOWN';
}

/** `POST /api/libraries/:id/ai-tag` — enfile un job `ai_tag` (201) et renvoie le job. */
export async function enqueueAiTag(libraryId: string, input: AiTagRequestInput): Promise<Job> {
  const res = await apiFetch(`/api/libraries/${libraryId}/ai-tag`, {
    method: 'POST',
    credentials: 'same-origin',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(input),
  });
  if (!res.ok) throw new AiApiError(res.status, await errorCodeOf(res));
  return jobSchema.parse(await res.json());
}
